import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import Content from './Content';
import axiosInstance, { BASE_URL } from '../../config/axios';
import { toast } from 'react-toastify';

// Mock data - سيتم استبداله بالداتا الحقيقية
const courseData = {
  id: 1,
  title: 'الرخصة المهنية',
  sections: [
    {
      id: 1,
      title: 'مقدمة في الإدارة',
      lessons: [
        { id: 1, title: 'مفهوم الإدارة', type: 'video', duration: '10:00' },
        { id: 2, title: 'أهمية الإدارة', type: 'video', duration: '15:00' },
        { id: 3, title: 'وظائف الإدارة', type: 'video', duration: '12:00' },
        { id: 4, title: 'حقيبة العلوم الإدارية', type: 'course' },
        { id: 8, title: 'قروب الدورة', type: 'course' }
      ]
    },
    {
      id: 2,
      title: 'التخطيط الإداري',
      lessons: [
        { id: 5, title: 'مفهوم التخطيط', type: 'video', duration: '08:00' },
        { id: 6, title: 'أنواع التخطيط', type: 'video', duration: '14:00' },
        { id: 7, title: 'اختبار التخطيط', type: 'quiz', duration: '20:00' }
      ]
    }
  ]
};

const CourseLearn = () => {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  // حالة البيانات
  const [apiCourseData, setApiCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // متغيرات الحالة للدورة
  const [openSection, setOpenSection] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showDetailedResults, setShowDetailedResults] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds
  const [isLoading, setIsLoading] = useState(false);
  const [openSections, setOpenSections] = useState([]);
  const [quizData, setQuizData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  
  // دالة جلب المشتريات وحفظها في sessionStorage
  const fetchPurchasedCourses = async () => {
    try {
      const response = await axiosInstance.get('/api/user/my-orders?type=course');
      const purchasedCourses = [];
      
      response.data.forEach(order => {
        if (order.status === 'accepted') {
          order.items.forEach(item => {
            if (item.course_id) {
              purchasedCourses.push(parseInt(item.course_id));
            }
          });
        }
      });
      
      // حفظ قائمة الكورسات المشتراة في sessionStorage
      sessionStorage.setItem('purchasedCourses', JSON.stringify([...new Set(purchasedCourses)]));
    } catch (error) {
      console.error('Error fetching purchased courses:', error);
    }
  };

  // دالة جلب بيانات الدورة من API
  const fetchCourseContent = async () => {
    try {
      setLoading(true);
      
      // جلب المشتريات أولاً
      await fetchPurchasedCourses();
      
      // تأكد من وجود معلومات المستخدم
      const token = sessionStorage.getItem('token');
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      
      // التحقق من صلاحيات المستخدم مبكراً
      const isCurrentUserAdmin = user?.role === 'admin';
      const isUserAuthenticated = !!token && !!user;

      // User authentication status
      
      // إرسال معلومات الصلاحيات في الهيدر
      const headers = { 'X-Public-Access': 'true' };
      
      // إذا كان المستخدم مشرف، نضيف معلومات الصلاحيات
      if (isUserAuthenticated && isCurrentUserAdmin) {
        headers['X-Admin-Access'] = 'true';
      }
      
      const response = await axiosInstance.get(`/api/courses/${courseId}/content`, { 
        params: { _t: Date.now() },
        headers: headers
      });
      
      // Course content data
      
      if (response.data) {
        // تحديث البيانات من API
        const apiData = response.data;
        // API data received
        
        // تحديد البيانات المناسبة من الاستجابة
        let sections = [];
        
        if (apiData.sections) {
          sections = apiData.sections;
        } else if (apiData.content) {
          sections = apiData.content;
        } else if (Array.isArray(apiData)) {
          sections = apiData;
        }
        
        // فلتر الأقسام المنشورة فقط للطلاب
        sections = sections.filter(sec => sec.status === 'published');

        // طباعة محتويات الأقسام للتحقق
        // Sections count and content details
        
        // تحديث البيانات في الحالة
        setApiCourseData({
          title: apiData.title || '',
          sections: sections
        });
        
        // إذا كانت هناك أقسام، حاول احترام lessonId من الرابط أولاً ثم استخدم السلوك الافتراضي
        if (sections.length > 0) {
          const lid = lessonId ? lessonId.toString() : null;
          let matched = null;
          let matchedSectionId = null;

          if (lid) {
            // المرحلة الأولى: نجمع كل التطابقات من items عبر جميع الأقسام ونفضّل الاختبار
            const itemsMatches = [];
            for (const sec of sections) {
              if (Array.isArray(sec.items)) {
                const candidates = sec.items.filter(it => it?.id?.toString() === lid);
                if (candidates.length > 0) {
                  // احتفظ بالمرشح مع مرجع القسم
                  for (const cand of candidates) {
                    const inferredType = cand.type || (cand.questions_count != null ? 'quiz' : 'lesson');
                    itemsMatches.push({ item: { ...cand, type: inferredType }, sectionId: sec.id });
                  }
                }
              }
            }
            if (itemsMatches.length > 0) {
              try { console.info('[Learn Match] itemsMatches=', itemsMatches.map(m => ({id: m.item.id, type: m.item.type, sectionId: m.sectionId}))); } catch (e) {}
              // استخدم تلميح النوع إن وُجد لفض التعارض (resource/lesson/quiz...)
              let hintType = null;
              try { hintType = sessionStorage.getItem('learn_target_type'); } catch (_) {}
              // طبيع "course" إلى "resource" إن لزم
              if (hintType === 'course') hintType = 'resource';
              // عند وجود hintType، نعمل تصفية للتطابقات إلى نفس النوع لتفادي ظهور تطابق مزدوج في اللوج
              let filteredMatches = itemsMatches;
              if (hintType) {
                filteredMatches = itemsMatches.filter(m => (m.item.type || '').toString() === hintType.toString());
              }
              let preferred = null;
              if (filteredMatches.length > 0) {
                preferred = filteredMatches.find(m => m.item.type === 'quiz') || filteredMatches[0];
              }
              // إن لم يوجد تلميح أو لم يطابق، نُفضّل الاختبار ثم الأول كما كان
              if (!preferred) {
                preferred = itemsMatches.find(m => m.item.type === 'quiz') || itemsMatches[0];
              }
              matched = preferred.item;
              matchedSectionId = preferred.sectionId;
              try { console.info('[Learn Match] chosen from items=', {id: matched.id, type: matched.type, sectionId: matchedSectionId, hintType}); } catch (e) {}
            }

            // المرحلة الثانية: إن لم نجد في items، نبحث عبر كل الأقسام حسب النوع
            if (!matched) {
              // exams أولاً
              for (const sec of sections) {
                if (!matched && Array.isArray(sec.exams)) {
                  const found = sec.exams.find(it => it?.id?.toString() === lid);
                  if (found) { matched = { ...found, type: 'quiz' }; matchedSectionId = sec.id; }
                }
              }
              if (matched) { try { console.info('[Learn Match] chosen from exams fallback=', {id: matched.id, type: matched.type, sectionId: matchedSectionId}); } catch (e) {} }
            }
            if (!matched) {
              // ثم الدروس
              for (const sec of sections) {
                if (!matched && Array.isArray(sec.lessons)) {
                  const found = sec.lessons.find(it => it?.id?.toString() === lid);
                  if (found) { matched = { ...found, type: found.type || 'lesson' }; matchedSectionId = sec.id; }
                }
              }
              if (matched) { try { console.info('[Learn Match] chosen from lessons fallback=', {id: matched.id, type: matched.type, sectionId: matchedSectionId}); } catch (e) {} }
            }
            if (!matched) {
              // ثم الموارد
              for (const sec of sections) {
                if (!matched && Array.isArray(sec.resources)) {
                  const found = sec.resources.find(it => it?.id?.toString() === lid);
                  if (found) { matched = { ...found, type: 'resource' }; matchedSectionId = sec.id; }
                }
              }
              if (matched) { try { console.info('[Learn Match] chosen from resources fallback=', {id: matched.id, type: matched.type, sectionId: matchedSectionId}); } catch (e) {} }
            }
            if (!matched) {
              // ثم القروبات
              for (const sec of sections) {
                if (!matched && Array.isArray(sec.groups)) {
                  const found = sec.groups.find(it => it?.id?.toString() === lid);
                  if (found) { matched = { ...found, type: 'group' }; matchedSectionId = sec.id; }
                }
              }
              if (matched) { try { console.info('[Learn Match] chosen from groups fallback=', {id: matched.id, type: matched.type, sectionId: matchedSectionId}); } catch (e) {} }
            }
          }

          if (matched) {
            setOpenSection(matchedSectionId);
            setOpenSections([matchedSectionId]);
            setCurrentLesson(matched);
          } else {
            const firstSection = sections[0];
            setOpenSection(firstSection.id);
            setOpenSections([firstSection.id]);
            
            if (firstSection.lessons && firstSection.lessons.length > 0) {
              setCurrentLesson(firstSection.lessons[0]);
            } else if (firstSection.exams && firstSection.exams.length > 0) {
              setCurrentLesson({ ...firstSection.exams[0], type: 'quiz' });
            } else if (firstSection.resources && firstSection.resources.length > 0) {
              setCurrentLesson({ ...firstSection.resources[0], type: 'resource' });
            } else if (firstSection.groups && firstSection.groups.length > 0) {
              setCurrentLesson({ ...firstSection.groups[0], type: 'group' });
            }
          }
        }
      }
    } catch (err) {
      // Error fetching course content
      if (err.response && err.response.status === 403 && err.response.data && err.response.data.is_expired) {
        // الدورة منتهية
        toast.error('هذه الدورة منتهية ولا يمكن الوصول إلى محتواها');
        // توجيه المستخدم مباشرة إلى صفحة تفاصيل الدورة
        navigate(`/courses/${courseId}`);
      } else if (err.response && err.response.status === 403 && err.response.data && err.response.data.installment_blocked) {
        // الدورة مقفولة بسبب تأخر القسط
        toast.error('الدورة مقفولة بسبب وجود قسط متأخر');
        navigate(`/courses/${courseId}`);
      } else {
        // أخطاء أخرى
        setError('حدث خطأ أثناء تحميل محتوى الدورة');
        toast.error('حدث خطأ أثناء تحميل محتوى الدورة');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // استدعاء دالة جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    if (courseId) {
      fetchCourseContent();
    }
  }, [courseId]);

  // معالجة بيانات الاختبار عند تغيير currentLesson
  useEffect(() => {
    if (currentLesson && currentLesson.type === 'quiz') {
      if (currentLesson.questions && currentLesson.questions.length > 0) {
        const processedQuestions = currentLesson.questions.map(q => {
          const rawImg = q.image || q.question_image || q.image_url || q.image_path || null;
          const isAbs = rawImg ? /^https?:\/\//i.test(rawImg) : false;
          const image = rawImg ? (isAbs ? rawImg : `${BASE_URL}${rawImg.startsWith('/') ? '' : '/'}${rawImg}`) : null;
          const answers = (q.answers || []).map(a => {
            const aRaw = a.image || a.image_path || null;
            const aAbs = aRaw ? /^https?:\/\//i.test(aRaw) : false;
            const aImg = aRaw ? (aAbs ? aRaw : `${BASE_URL}${aRaw.startsWith('/') ? '' : '/'}${aRaw}`) : null;
            return { id: a.id.toString(), text: a.text, image: aImg };
          });
          return {
            id: q.id,
            text: q.text,
            image,
            notification: q.notification,
            correctAnswer: q.correct_answer.toString(),
            answers
          };
        });
        setQuizData({ questions: processedQuestions });
      } else {
        setQuizData({ questions: [] });
      }
    }
  }, [currentLesson]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Reset quiz state when lesson changes
  const handleQuizReset = () => {
    setIsQuizStarted(false);
    setCurrentAnswer(null);
    setQuizAnswers([]);
    setShowResults(false);
    setShowDetailedResults(false);
    setCurrentQuestionIndex(0);
    // استخدام مدة الاختبار الحقيقية بالدقائق إذا كانت متوفرة، وإلا استخدام 30 دقيقة كقيمة افتراضية
    let duration = 20; // القيمة الافتراضية 20 دقيقة
    
    // التحقق من وجود مدة في الدرس الحالي
    if (currentLesson && typeof currentLesson.duration === 'number' && currentLesson.duration > 0) {
      duration = currentLesson.duration;
    }
    
    // Quiz duration in minutes
    // تحويل الدقائق إلى ثواني
    const timeInSeconds = Math.floor(duration * 60);
    // Quiz duration in seconds
    setTimeLeft(timeInSeconds);
    
    // تحديث بيانات الاختبار إذا كانت متوفرة في الدرس الحالي
    if (currentLesson?.questions && currentLesson.questions.length > 0) {
      // Using real quiz questions
      
      // معالجة البيانات بنفس الطريقة المستخدمة في handleLessonSelect
      const processedQuestions = currentLesson.questions.map(q => {
        const rawImg = q.image || q.question_image || q.image_url || q.image_path || null;
        const isAbs = rawImg ? /^https?:\/\//i.test(rawImg) : false;
        const image = rawImg ? (isAbs ? rawImg : `${BASE_URL}${rawImg.startsWith('/') ? '' : '/'}${rawImg}`) : null;
        const answers = (q.answers || []).map(a => {
          const aRaw = a.image || a.image_path || null;
          const aAbs = aRaw ? /^https?:\/\//i.test(aRaw) : false;
          const aImg = aRaw ? (aAbs ? aRaw : `${BASE_URL}${aRaw.startsWith('/') ? '' : '/'}${aRaw}`) : null;
          return { id: a.id.toString(), text: a.text, image: aImg };
        });
        return {
          id: q.id,
          text: q.text,
          image,
          notification: q.notification,
          correctAnswer: q.correct_answer.toString(), // تحويل إلى سلسلة نصية للتأكد من المقارنة الصحيحة
          answers
        };
      });
      
      // Processed questions data
      
      setQuizData({ questions: processedQuestions });
    }
  };

  // Handle showing results with animation
  const handleShowResults = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowResults(true);
    }, 1500);
  };

  
  // Timer countdown
  useEffect(() => {
    let timer;
    if (isQuizStarted && !showResults && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleShowResults();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isQuizStarted, showResults, timeLeft]);

  // Format time to MM:SS
  const formatTime = (seconds) => {
    // نتأكد أن الثواني عدد صحيح
    seconds = Math.floor(seconds);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (answerId) => {
    // تحويل معرف الإجابة إلى سلسلة نصية للتأكد من المقارنة الصحيحة
    const answerIdString = answerId.toString();
    // Selected answer
    
    setCurrentAnswer(answerIdString);
    const newAnswers = [...quizAnswers];
    newAnswers[currentQuestionIndex] = answerIdString;
    setQuizAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    const totalQuestions = quizData?.questions?.length || currentLesson.questions_count || 0;
    if (currentQuestionIndex === totalQuestions - 1) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setShowResults(true);
      }, 1500);
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer(null);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setCurrentAnswer(quizAnswers[currentQuestionIndex - 1] || null);
    }
  };

  const calculateScore = () => {
    let correctCount = 0;
    
    // Diagnostic data
    
    quizAnswers.forEach((answer, index) => {
      if (quizData?.questions?.[index]) {
        // Question and answer comparison
        
        // مقارنة الإجابة المختارة مع الإجابة الصحيحة
        if (answer === quizData.questions[index].correctAnswer) {
          correctCount++;
          // Correct answer
        } else {
          // Wrong answer
        }
      }
    });
    
    const totalQuestions = quizData?.questions?.length || currentLesson.questions_count || 0;
    // Correct answers count
    
    return {
      correct: correctCount,
      total: totalQuestions,
      percentage: totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0
    };
  };

  const toggleSection = (sectionId) => {
    if (openSections.includes(sectionId)) {
      setOpenSections([]);
    } else {
      setOpenSections([sectionId]);
    }
  };

  // Mobile-only sidebar behavior
  useEffect(() => {
    if (window.innerWidth <= 768 && hasUserInteracted) {
      setIsSidebarOpen(false);
    }
  }, [currentLesson, hasUserInteracted]);

  const handleLessonSelect = async (lesson) => {
    // 1. التحقق من الصلاحيات أولاً
    const userPermissions = sessionStorage.getItem('userPermissions');
    const hasViewAllCoursesPermission = userPermissions ? JSON.parse(userPermissions).includes('view_all_courses') : false;
    const isCurrentUserAdmin = hasViewAllCoursesPermission;
    
    // 2. لو مدير يعدي من غير ما يتحقق
    if (isCurrentUserAdmin) {
      navigateToLesson(lesson);
      return;
    }

    // 3. لو درس مجاني يعدي من غير ما يتحقق
    const isFree = lesson.is_free === true || lesson.is_free === 1 || lesson.is_free === '1';
    if (isFree) {
      navigateToLesson(lesson);
      return;
    }

    // 4. لو درس مدفوع بنتحقق من المشتريات
    try {
      // نجيب قائمة المشتريات
      const orders = await axiosInstance.get(`/api/user/my-orders?type=course`);
      
      // نتحقق إذا كانت الدورة موجودة في قائمة المشتريات
      const hasPurchased = orders.data.some(order => 
        order.status === 'accepted' && 
        order.items.some(item => item.course_id?.toString() === courseId.toString())
      );
      
      if (hasPurchased) {
        navigateToLesson(lesson);
      } else {
        // رسائل مختلفة حسب نوع المحتوى
        if (lesson.type === 'course') {
          toast.error('يجب الاشتراك في الدورة للوصول للحقيبة');
        } else if (lesson.type === 'group') {
          toast.error('يجب الاشتراك في الدورة للوصول للقروب');
        } else if (lesson.type === 'quiz') {
          toast.error('يجب الاشتراك في الدورة للوصول إلى الاختبار');
        } else {
          toast.error('يجب الاشتراك في الدورة للوصول للمحتوى');
        }
      }
    } catch (error) {
      console.error('Error checking purchase:', error);
      toast.error('حدث خطأ، يرجى المحاولة لاحقاً');
    }
  };

  // دالة مساعدة للتنقل للدرس بعد التأكد من الصلاحيات
  const navigateToLesson = (lesson) => {
    // إعادة تعيين جميع حالات الاختبار أولاً
    setIsQuizStarted(false);
    setCurrentAnswer(null);
    setQuizAnswers([]);
    setShowResults(false);
    setShowDetailedResults(false);
    setCurrentQuestionIndex(0);
    
    // تعيين الدرس الحالي
    setCurrentLesson(lesson);
    setHasUserInteracted(true);
    
    // تحديث الرابط
    navigate(`/courses/${courseId}/learn/${lesson.id}`, { replace: false });
    
    // إذا كان الدرس من نوع اختبار
    if (lesson.type === 'quiz') {
      // ضبط الوقت بناءً على مدة الاختبار الحالي
      let duration = 20; // القيمة الافتراضية 20 دقيقة
      
      if (lesson && typeof lesson.duration === 'number' && lesson.duration > 0) {
        duration = lesson.duration;
      }
      
      // Quiz duration in minutes
      const timeInSeconds = Math.floor(duration * 60);
      // Quiz duration in seconds
      setTimeLeft(timeInSeconds);
      
      // تحديث بيانات الاختبار إذا كانت متوفرة
      if (lesson.questions && lesson.questions.length > 0) {
        // Real quiz questions
        // بيانات الأسئلة للتحقق من البيانات الواردة من الباك إند
        // Raw question data from backend
        
        // التحقق من شكل الإجابة الصحيحة
        const processedQuestions = lesson.questions.map(q => {
          // Question correct answer
          return {
            id: q.id,
            text: q.text,
            notification: q.notification,
            correctAnswer: q.correct_answer.toString(), // تحويل إلى سلسلة نصية للتأكد من المقارنة الصحيحة
            answers: q.answers.map(a => ({
              id: a.id.toString(), // تحويل إلى سلسلة نصية للتأكد من المقارنة الصحيحة
              text: a.text
            }))
          };
        });
        
        // Processed questions data
        
        setQuizData({
          questions: processedQuestions
        });
      } else {
        // إذا لم تكن هناك أسئلة، نضع قائمة أسئلة فارغة
        setQuizData({ questions: [] });
      }
    }
  };

  // عرض شاشة التحميل أثناء جلب البيانات
  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-xl font-medium text-gray-900 dark:text-white">جاري تحميل محتوى الدورة...</p>
        </div>
      </div>
    );
  }

  // عرض رسالة الخطأ إذا فشل تحميل البيانات
  if (error) {
    return (
      <div className="h-screen flex justify-center items-center bg-white dark:bg-gray-900">
        <div className="text-center p-6 max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">حدث خطأ</h2>
          <p className="mb-4 text-gray-700 dark:text-gray-300">{error}</p>
          <button 
            onClick={() => fetchCourseContent()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  // عرض محتوى الدورة
  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-900">
      <Navbar 
        theme={theme}
        toggleTheme={toggleTheme}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        courseTitle={apiCourseData?.title || courseData.title}
        navigate={navigate}
      />
      
      <div className="flex-1 flex overflow-hidden">
        <Sidebar 
          courseData={apiCourseData || courseData}
          openSection={openSection}
          setOpenSection={setOpenSection}
          currentLesson={currentLesson}
          setCurrentLesson={setCurrentLesson}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          openSections={openSections}
          toggleSection={toggleSection}
          handleLessonSelect={handleLessonSelect}
          courseId={courseId}
        />
        
        <Content 
          currentLesson={currentLesson}
          isQuizStarted={isQuizStarted}
          setIsQuizStarted={setIsQuizStarted}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          hasUserInteracted={hasUserInteracted}
          setHasUserInteracted={setHasUserInteracted}
          quizData={quizData}
          handleAnswerSelect={handleAnswerSelect}
          handleNextQuestion={handleNextQuestion}
          handlePreviousQuestion={handlePreviousQuestion}
          calculateScore={calculateScore}
          currentQuestionIndex={currentQuestionIndex}
          setCurrentQuestionIndex={setCurrentQuestionIndex}
          timeLeft={timeLeft}
          setTimeLeft={setTimeLeft}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          showResults={showResults}
          setShowResults={setShowResults}
          showDetailedResults={showDetailedResults}
          setShowDetailedResults={setShowDetailedResults}
          quizAnswers={quizAnswers}
          setQuizAnswers={setQuizAnswers}
          currentAnswer={currentAnswer}
          setCurrentAnswer={setCurrentAnswer}
          formatTime={formatTime}
          handleShowResults={handleShowResults}
          handleQuizReset={handleQuizReset}
          isSidebarOpen={isSidebarOpen}
        />
      </div>
    </div>
  );
};

export default CourseLearn;
